import { ListFilterBase, SortObject } from "generated/openApi";
import { useState, useRef } from "react";
import produce from "immer";

export const useList = <T extends ListFilterBase>(props: {
  listFilter: T;
  setListFilter: (value: T) => void;
  fetchList: (value: T) => void;
  fetchTotal?: (value: T) => void;
  onLoad?: (listFilter: T) => void;
}) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const loadList = (listFilter: T, options?: { resetPage?: boolean }) => {
    let updatedListFilter = { ...listFilter };
    if (options?.resetPage) {
      updatedListFilter.page = 1;
      props.setListFilter(updatedListFilter);
      tableContainerRef?.current?.scrollTo(0, 0);
    }

    props.fetchList(updatedListFilter);
    props.fetchTotal?.(updatedListFilter);
    props.onLoad?.(listFilter);
  };

  const applyPage = (page: number) => {
    const updatedListFilter = { ...props.listFilter, page: page };
    props.fetchList(updatedListFilter);
    props.setListFilter(updatedListFilter);
    tableContainerRef?.current?.scrollTo(0, 0);
  };

  const applySort = (sortObjects: SortObject[]) => {
    const updatedListFilter = { ...props.listFilter, page: 1, sortObjects: sortObjects };
    props.fetchList(updatedListFilter);
    props.setListFilter(updatedListFilter);
    tableContainerRef?.current?.scrollTo(0, 0);
  };

  const applyPageSize = (pageSize: number) => {
    const updatedListFilter = { ...props.listFilter, page: 1, pageSize: pageSize };
    props.fetchList(updatedListFilter);
    props.setListFilter(updatedListFilter);
  };

  const applySearch = (searchString: string) => {
    const updatedListFilter = { ...props.listFilter, page: 1, searchString: searchString };
    props.fetchList(updatedListFilter);
    props.fetchTotal?.(updatedListFilter);
    props.setListFilter(updatedListFilter);
    props.onLoad?.(updatedListFilter);
    tableContainerRef?.current?.scrollTo(0, 0);
  };

  const applySortMethod = (multiSort: boolean) => {
    if (!multiSort && props.listFilter.sortObjects && props.listFilter.sortObjects?.length > 1) {
      let updatedListFilter = { ...props.listFilter };
      updatedListFilter.sortObjects = [updatedListFilter.sortObjects?.[0]!];
      props.fetchList(updatedListFilter);
      props.setListFilter(updatedListFilter);
    }
  };

  const selectId = (id: number) => {
    if (!selectedIds.includes(id)) {
      setSelectedIds(
        produce((draft) => {
          draft.push(id);
        })
      );
    } else {
      setSelectedIds(
        produce((draft) => {
          draft.splice(draft.indexOf(id), 1);
        })
      );
    }
  };

  return {
    tableContainerRef: tableContainerRef,
    loadList: loadList,
    applyPage: applyPage,
    applyPageSize: applyPageSize,
    applySortMethod: applySortMethod,
    applySort: applySort,
    applySearch: applySearch,
    selectedIds: selectedIds,
    setSelectedIds: setSelectedIds,
    selectId: selectId,
  };
};
